import {
    Button,
    DisplayOrInput,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    RoleSelector,
    ToggleSwitch,
    ConfirmationModal,
    Field,
    useFingerprintDisplay,
} from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { OuterModule } from "services";
import { useCreateOuterModule, useDeleteOuterModule, useEditOuterModule } from "..";

export interface IOuterModuleDetailsModal {
    module?: OuterModule;
    totalModules: number;
    isOpen: boolean;
    handleClose: () => void;
}

const emptyModule: OuterModule = {
    id: "",
    name: "",
    description: "",
    url: "",
    roles: [],
    isActive: true,
    orderNumber: 1,
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
    isPublic: false,
};

type ModuleProperty = keyof OuterModule;

type ModuleErrors = Partial<Record<ModuleProperty, string>>;

export const OUTER_MODULE_DESCRIPTION_MAX_LENGTH = 400;

const validateModule = (module: OuterModule, maxOrderNumber: number): ModuleErrors => {
    const errors: ModuleErrors = {};
    if (!module.name?.trim()) errors.name = "Naziv modula je obavezan";
    if (!module.url) errors.url = "URL modula je obavezan";
    if (!module.url.match(/^(http|https):\/\/[^ "]+$|^(www)\.[^ "]+$/))
        errors.url = "URL nije ispravnog formata";
    if (!module.orderNumber) errors.orderNumber = "Redni broj modula je obavezan";
    else if (module.orderNumber < 1 || module.orderNumber > maxOrderNumber)
        errors.orderNumber = `Redni broj modula mora biti između 1 i ${maxOrderNumber}`;
    if (!module.isPublic && !module.roles.length)
        errors.roles = "Uloge su obavezne ako modul nije javan";

    return errors;
};

export const OuterModuleDetailsModal = ({
    module: selectedModule,
    totalModules,
    isOpen,
    handleClose,
}: IOuterModuleDetailsModal) => {
    const [module, setModule] = useState<OuterModule>(emptyModule);
    const [errors, setErrors] = useState<ModuleErrors>({});
    const [isEdit, setIsEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateOuterModule();
    const edit = useEditOuterModule();
    const remove = useDeleteOuterModule();

    useEffect(() => {
        setModule(selectedModule ?? emptyModule);
    }, [selectedModule]);

    useEffect(() => {
        setErrors({});
        setIsEdit(false);
        if (!isOpen) setModule(emptyModule);
    }, [isOpen]);

    const isAdd = useMemo(() => !selectedModule, [selectedModule]);

    const maxOrderNumber = useMemo(
        () => (isAdd ? totalModules + 1 : totalModules),
        [isAdd, totalModules]
    );

    const title = useMemo(() => {
        if (isAdd) return "Novi modul";
        if (isEdit) return "Uredi modul";
        return module?.name || "Detalji modula";
    }, [isAdd, isEdit, module?.name]);

    const changeModuleProperty = (
        property: ModuleProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prev => ({ ...prev, [property]: undefined }));
        setModule(prev => ({ ...(prev ?? emptyModule), [property]: event.target.value }));
    };

    const isValid = (property: ModuleProperty) => !errors[property];
    const getErrorMsg = (property: ModuleProperty) => errors[property] ?? "";

    const descriptionMaxLengthReached =
        (module?.description?.length ?? 0) >= OUTER_MODULE_DESCRIPTION_MAX_LENGTH;
    const descriptionErrorMessage =
        (isAdd || isEdit) && descriptionMaxLengthReached
            ? "Maksimalan broj znakova je dosegnut."
            : undefined;

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        setModule(selectedModule ?? emptyModule);
    };

    const onSave = async () => {
        const newErrors = validateModule(module, maxOrderNumber);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const mutation = isAdd ? create.mutateAsync : edit.mutateAsync;
        await mutation(module);
        handleClose();
    };

    const onDelete = async () => {
        if (!module) return;
        await remove.mutateAsync(module.id);
        setShowDelete(false);
        handleClose();
    };

    const { created, updated, hasCreated, hasUpdated, hasBoth } = useFingerprintDisplay({
        fingerprint: module,
        containerClassName: "modal-section no-border",
        labelClassName: "modal-details-label",
        textClassName: "modal-details-text",
    });

    const fingerprintDisplay = hasBoth ? (
        <>
            <div className="modal-section narrow">{created}</div>
            <div className="modal-section narrow">{updated}</div>
            <div className="modal-section narrow" />
        </>
    ) : hasUpdated ? (
        <div className="modal-section wide">{updated}</div>
    ) : hasCreated ? (
        <div className="modal-section wide">{created}</div>
    ) : null;

    return (
        <Modal
            isActive={isOpen}
            className="outer-module-details-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                module && (
                    <ModalFooter>
                        {isAdd || isEdit ? (
                            <>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning"
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                            </>
                        )}
                    </ModalFooter>
                )
            }
        >
            <div className="outer-module-details">
                <div className="modal-section narrow">
                    <Field name="Naziv modula" errorMessage={getErrorMsg("name")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {module?.name}
                            <Input
                                id="name"
                                type="text"
                                size="small"
                                name="name"
                                isValid={isValid("name")}
                                value={module?.name}
                                onChange={changeModuleProperty.bind(null, "name")}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                <div className="modal-section narrow">
                    <Field name="Redni broj" errorMessage={getErrorMsg("orderNumber")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {module?.orderNumber}
                            <Input
                                id="orderNumber"
                                type="number"
                                size="small"
                                name="orderNumber"
                                isValid={isValid("orderNumber")}
                                min={1}
                                max={maxOrderNumber}
                                value={module?.orderNumber}
                                onChange={changeModuleProperty.bind(null, "orderNumber")}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                <div className="modal-section narrow">
                    <Field name="Status">
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {module?.isActive ? "Aktivan" : "Neaktivan"}
                            <ToggleSwitch
                                value={String(module?.isActive)}
                                checked={module?.isActive}
                                onChange={() =>
                                    setModule(prev => ({
                                        ...prev,
                                        isActive: !prev.isActive,
                                    }))
                                }
                                htmlFor="status"
                                valueNames={{ true: "Aktivan", false: "Neaktivan" }}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                <div className="modal-section wide modal-section-description">
                    <Field name="Opis modula" errorMessage={descriptionErrorMessage}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {module?.description}
                            <Input
                                isArea
                                id="description"
                                type="text"
                                size="small"
                                name="description"
                                value={module?.description}
                                maxLength={OUTER_MODULE_DESCRIPTION_MAX_LENGTH}
                                onChange={changeModuleProperty.bind(null, "description")}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                <div className="modal-section wide">
                    <Field name="URL" errorMessage={getErrorMsg("url")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            <a href={module?.url} target="_blank" rel="noopener noreferrer">
                                {module?.url}
                            </a>
                            <Input
                                id="url"
                                type="text"
                                size="small"
                                name="url"
                                isValid={isValid("url")}
                                value={module?.url}
                                onChange={changeModuleProperty.bind(null, "url")}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                <div className="modal-section wide">
                    <Field name="JAVNO DOSTUPNO">
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {module?.isPublic ? "Da" : "Ne"}
                            <ToggleSwitch
                                value={String(module?.isPublic)}
                                checked={module?.isPublic}
                                onChange={() =>
                                    setModule(prev => ({
                                        ...prev,
                                        isPublic: !prev.isPublic,
                                    }))
                                }
                                htmlFor="isPublic"
                                valueNames={{ true: "Da", false: "Ne" }}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                {!module.isPublic && (
                    <div className="modal-section wide">
                        <Field name="Uloge" errorMessage={getErrorMsg("roles")}>
                            <RoleSelector
                                disabled={!isAdd && !isEdit}
                                columns={3}
                                value={module?.roles}
                                setValues={roles => setModule(prev => ({ ...prev, roles }))}
                            />
                        </Field>
                    </div>
                )}

                {fingerprintDisplay}
            </div>

            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati modul ${module?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
